import { useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { useQueryClient } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { useDisplayCurrency } from "resources/currencies"
import { combineStatuses, combineTanStackStatuses, ERROR, isError, isLoading, LOADING, OK } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"
import { extractFirstGraphqlErrorMessage, rejectOnGraphqlErrors, rejectOnRequestLevelGraphqlErrors } from "./extract-graphql-error-message"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import {
  getInstrumentSuffixForIntradayContributionPrefix,
  mergeContributorTrees,
} from "./contributors-data-integrator.utils"
import { useIntradayContributorsEnhancement } from "./hooks/use-intraday-contributors-enhancement"
import { useFieldScopedErrorWarning } from "./hooks/use-field-scoped-error-warning"

const EMPTY_ARRAY = []
const NO_CURRENCY_SOURCE_PROPS = {}
const PENDING_CURRENCY_PROPS = { currency: null, accountCurrency: null }

function makeUnavailableCurrencyProps(accountCurrency) {
  return { currency: null, accountCurrency, isCurrencyUnavailable: true }
}

function getCurrencyProps({ hasCurrencySource, currencyStatus, currency, accountCurrency }) {
  if (!hasCurrencySource) return NO_CURRENCY_SOURCE_PROPS
  if (isLoading(currencyStatus)) return PENDING_CURRENCY_PROPS
  if (isError(currencyStatus)) return makeUnavailableCurrencyProps(accountCurrency)
  return { currency, accountCurrency }
}

// No `[]` fallback — an absent key must stay absent so react-query rejects it rather than silently
// collapsing every keyless gatherer onto one cache entry.
function getCanonicalQueryKey({ key, graphqlKey }) {
  return graphqlKey ?? key
}

export {
  getNormalPerformanceAutoRefreshWarningState,
  shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate,
  shouldSubscribeToIntradayPriceTicks,
  mergeContributorTrees,
} from "./contributors-data-integrator.utils"

export default function ContributorsDataIntegrator(props) {
  const {
    gatherers,
    metrics,
    Renderer,
    onModelProviderIds,
    // rendererProps may not include: status, data, metrics, errorMessage, currency, accountCurrency,
    // isIntradayContributionRefreshing, intradayPricingSummary, intradayPricePrefix —
    // the integrator owns those and spreads rendererProps before them so they can't be shadowed.
    rendererProps = {},
    instrumentsWithMetadata,
    modelId,
    mainInstrument,
    resourceMetadataName,
  } = props
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const account = useAccountAlias()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  const nonIntradayGatherers = useMemo(() => (gatherers || []).filter((gatherer) => gatherer.type !== "intraday"), [gatherers])
  const intradayGatherers = useMemo(() => (gatherers || []).filter((gatherer) => gatherer.type === "intraday"), [gatherers])
  const intradayContributionQueryIndexes = useMemo(() => {
    return nonIntradayGatherers.reduce((indexes, gatherer, index) => {
      if (getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null) {
        indexes.push(index)
      }
      return indexes
    }, [])
  }, [nonIntradayGatherers])

  const combine = useCallback((results) => {
    const status = combineTanStackStatuses(results)
    const dataResults = map(results, (result) => result?.data).filter(Boolean)
    const data = dataResults.length > 0 ? mergeContributorTrees(dataResults) : null
    let isFetchingIntradayContributionQueries = false
    let isAnyQueryPending = false

    for (let queryIndex = 0; queryIndex < results.length; queryIndex++) {
      const queryResult = results[queryIndex]
      if (!queryResult) {
        continue
      }

      if (queryResult.status === "pending") {
        isAnyQueryPending = true
      }

      const isContributionQuery = intradayContributionQueryIndexes.includes(queryIndex)
      if (isContributionQuery) {
        if (queryResult.isFetching && queryResult.dataUpdatedAt > 0) {
          isFetchingIntradayContributionQueries = true
        }
      }
    }

    const errorMessage = status === ERROR ? extractFirstGraphqlErrorMessage(results) : null

    return {
      status,
      data,
      errorMessage,
      isFetchingIntradayContributionQueries,
      isAnyQueryPending,
    }
  }, [intradayContributionQueryIndexes])

  // Only gatherers opting in via `tolerateFieldScopedErrors` resolve on a field-scoped error instead of rejecting — everyone else keeps the strict default.
  // Reset key is a stable string built from query identity, not the gatherers array itself — nonIntradayGatherers gets
  // a fresh reference on nearly every render, which would otherwise reset the dedup on every UI interaction.
  const gathererResetKey = useMemo(
    () => nonIntradayGatherers
      .filter(({ tolerateFieldScopedErrors }) => tolerateFieldScopedErrors)
      .map((gatherer) => JSON.stringify(getCanonicalQueryKey(gatherer) ?? EMPTY_ARRAY))
      .join("|"),
    [nonIntradayGatherers],
  )
  const warnIfDegraded = useFieldScopedErrorWarning(gathererResetKey)

  const queries = useMemo(() => map(nonIntradayGatherers, (gatherer) => ({
    queryKey: getCanonicalQueryKey(gatherer),
    queryFn: ({ signal }) => new Promise((resolve, reject) => {
      const { query, variables, tolerateFieldScopedErrors, fieldScopedErrorWarning } = gatherer
      const onResolve = tolerateFieldScopedErrors
        ? (result) => {
          warnIfDegraded(result, fieldScopedErrorWarning)
          resolve(result)
        }
        : resolve
      const errorPolicy = tolerateFieldScopedErrors ? rejectOnRequestLevelGraphqlErrors : rejectOnGraphqlErrors
      dispatch(Creators.graphql(account, query, variables, errorPolicy(onResolve, reject), reject, signal))
    }),
    select: gatherer.select,
  })), [nonIntradayGatherers, dispatch, account, warnIfDegraded])

  const {
    status,
    data,
    errorMessage,
    isFetchingIntradayContributionQueries,
    isAnyQueryPending,
  } = useCombinedQueries(queries, combine)

  const {
    data: enhancedData,
    status: intradayStatus,
    metrics: enhancedMetrics,
    modelProviderIds,
    isIntradayContributionRefreshing,
    intradayPricingSummary,
    intradayPricePrefix,
  } = useIntradayContributorsEnhancement({
    nonIntradayGatherers,
    intradayGatherers,
    metrics,
    data,
    status,
    isFetchingIntradayContributionQueries,
    isAnyQueryPending,
    isIntradayAnalyticsEnabled,
    instrumentsWithMetadata,
    queryClient,
  })

  const hasCurrencySource = Boolean(modelId && mainInstrument?.id && mainInstrument?.type)
  const [currency, currencyStatus, accountCurrency] = useDisplayCurrency(
    hasCurrencySource ? modelId : null,
    hasCurrencySource ? mainInstrument : null,
    resourceMetadataName,
  )
  const isCurrencyPending = hasCurrencySource && isLoading(currencyStatus)
  const rendererStatus = combineStatuses([intradayStatus, isCurrencyPending ? LOADING : OK])
  const currencyProps = getCurrencyProps({ hasCurrencySource, currencyStatus, currency, accountCurrency })

  useEffect(() => {
    if (typeof onModelProviderIds !== "function") {
      return
    }
    onModelProviderIds(modelProviderIds || EMPTY_ARRAY)
  }, [modelProviderIds, onModelProviderIds])

  return (
    // rendererProps spreads first so the integrator's computed outputs below are
    // authoritative — a caller can't shadow the live data/status/intraday values.
    <Renderer
      {...rendererProps}
      status={rendererStatus}
      data={enhancedData}
      metrics={enhancedMetrics}
      errorMessage={errorMessage}
      isIntradayContributionRefreshing={isIntradayContributionRefreshing}
      intradayPricingSummary={intradayPricingSummary}
      intradayPricePrefix={intradayPricePrefix}
      {...currencyProps}
    />
  )
}

const GRAPHQL_GATHERER_SHAPE = PropTypes.shape({
  key: PropTypes.arrayOf(PropTypes.string).isRequired,
  graphqlKey: PropTypes.arrayOf(PropTypes.string),
  query: PropTypes.string.isRequired,
  variables: PropTypes.object.isRequired,
  select: PropTypes.func.isRequired,
  tolerateFieldScopedErrors: PropTypes.bool,
  fieldScopedErrorWarning: PropTypes.string,
})

const INTRADAY_GATHERER_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf(["intraday"]).isRequired,
  key: PropTypes.arrayOf(PropTypes.string).isRequired,
  modelId: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  streamType: PropTypes.oneOf(["prices", "standalone-performance"]).isRequired,
  period: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  freshnessOnly: PropTypes.bool,
})

ContributorsDataIntegrator.propTypes = {
  gatherers: PropTypes.arrayOf(PropTypes.oneOfType([GRAPHQL_GATHERER_SHAPE, INTRADAY_GATHERER_SHAPE])).isRequired,
  metrics: PropTypes.array.isRequired,
  Renderer: PropTypes.func.isRequired,
  rendererProps: PropTypes.object,
  onModelProviderIds: PropTypes.func,
  instrumentsWithMetadata: PropTypes.object,
  modelId: PropTypes.string,
  mainInstrument: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
  }),
  resourceMetadataName: PropTypes.string,
}
